import React, { useEffect, useMemo, useRef, useState } from "react";
import AIResponse from "./AIResponse";
import GeneralFeedbackModal from "./GeneralFeedbackModal";
import classes from "./DTMAIAgent.module.css";
import UserPrompt from "./UserPrompt";
import { API_BASE_URL, apiUrl } from "../configs/api";
import {
  MAX_PROMPT_CHARS,
  validatePromptInput,
} from "../constants/promptGuardrails";
import {
  findOperationHints,
  getBackendOperationMetadata,
} from "../util/operationHints";

const LOADING_STEPS = [
  "Understanding your question...",
  "Finding the relevant IDP data...",
  "Selecting the right assessment rounds...",
  "Preparing the data for analysis...",
  "Analyzing the displacement figures...",
  "Summarizing the results...",
];

const LOADING_STEP_DURATION_MS = 2500;

const initialVizState = {
  vizStatus: "idle",
  vizSpec: null,
  vizError: null,
  vizMeta: null,
};

const ANALYSIS_DOMAINS = [
  {
    key: "Trend",
    label: "Trend",
    desc: "Analyse how IDP numbers change over time across locations, reasons, or operations.",
    questions: [
      "Female IDP trend in Nigeria in 2024",
      "What is the trend of conflict IDPs in Sudan from 2022 to 2024",
      "Annual trend of idps in Haiti from 2015 to 2023",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <polyline points="4,28 10,20 16,23 23,14 30,10" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points="24,8 30,10 28,16" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    key: "Caseload",
    label: "Caseload",
    desc: "Explore total displaced population figures by country, region, or period.",
    questions: [
      "Total IDPs in Afghanistan in 2026",
      // "Displaced population in East Africa in 2023",
      "Number of IDPs in Syria as of 2025",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <circle cx="18" cy="11" r="4" stroke="#0033a0" strokeWidth="3" />
        <path d="M11 30 Q11 22 18 22 Q25 22 25 30" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" fill="none" />
        <circle cx="6" cy="14" r="3" stroke="#0033a0" strokeWidth="2.5" fill="none" />
        <path d="M1 29 Q1 23 6 23" stroke="#0033a0" strokeWidth="2.5" strokeLinecap="round" fill="none" />
        <circle cx="30" cy="14" r="3" stroke="#0033a0" strokeWidth="2.5" fill="none" />
        <path d="M35 29 Q35 23 30 23" stroke="#0033a0" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      </svg>
    ),
  },
  {
    key: "Ranking",
    label: "Ranking",
    desc: "Rank countries or regions by IDP figures for a given year or timeframe.",
    questions: [
      "Top 5 countries by IDP caseload in 2023",
      "Top 3 MENA countries by displacement in 2024",
      "Largest displacement crises in 2025",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <rect x="4" y="8" width="7" height="24" rx="2" stroke="#0033a0" strokeWidth="3" />
        <rect x="15" y="15" width="7" height="17" rx="2" stroke="#0033a0" strokeWidth="3" />
        <rect x="26" y="22" width="7" height="10" rx="2" stroke="#0033a0" strokeWidth="3" />
      </svg>
    ),
  },
  {
    key: "Constraints",
    label: "Constraints",
    desc: "Filter results by displacement reason, operation type, or data availability.",
    questions: [
      "Idp countries with more than 2million idps",
      "Countries with less than 500,000 female idps",
      "Idp countries with more than 650,000 idps driven by conflict",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <path d="M5 8 L15 20 L15 30 L21 27 L21 20 L31 8 Z" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    key: "Comparison",
    label: "Comparison",
    desc: "Compare IDP figures between two countries, regions, or time periods.",
    questions: [
      "Compare Sudan IDPs between 2022 and 2024",
      "Compare Hiaiti IDPs 2023 and 2024",
      "Compare Ukraine IDP data 2022 and 2026",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <circle cx="10" cy="20" r="4" stroke="#0033a0" strokeWidth="3" />
        <line x1="18" y1="6" x2="18" y2="34" stroke="#0033a0" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="27" cy="20" r="9" stroke="#0033a0" strokeWidth="3" />
      </svg>
    ),
  },
  {
    key: "Average",
    label: "Average",
    desc: "Compute mean IDP figures across years, operations, or locations.",
    questions: [
      "Average yearly IDPs in Somalia",
      "What is the average female IDPs in Ethiopia in 2024",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <polyline points="4,26 10,16 16,21 22,12 28,18 33,13" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <line x1="4" y1="20" x2="33" y2="20" stroke="#0033a0" strokeWidth="2.5" strokeDasharray="4,3" strokeLinecap="round" />
      </svg>
    ),
  },
];

const INDICATORS = [
  {
    key: "Location",
    label: "Location",
    desc: "Reference a country, region, or admin area in your question.",
    questions: [
      "IDPs in Nigeria in 2024",
      "Displacement across East Africa",
      "Which admin areas in Syria have the most IDPs?",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <path d="M18 4 C12 4 8 9 8 14 C8 21 18 32 18 32 C18 32 28 21 28 14 C28 9 24 4 18 4 Z" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="18" cy="14" r="3.5" stroke="#0033a0" strokeWidth="3" />
      </svg>
    ),
  },
  {
    key: "No of IDPs",
    label: "No of IDPs",
    desc: "Ask for displaced population counts and figures.",
    questions: [
      "Total IDPs in Afghanistan",
      "How many IDPs in Sudan in 2023?",
      // "Number of displaced people across MENA",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <circle cx="10" cy="11" r="3.5" stroke="#0033a0" strokeWidth="3" />
        <path d="M3 30 Q3 23 10 23 Q17 23 17 30" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" fill="none" />
        <circle cx="26" cy="11" r="3.5" stroke="#0033a0" strokeWidth="3" />
        <path d="M19 30 Q19 23 26 23 Q33 23 33 30" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" fill="none" />
      </svg>
    ),
  },
  {
    key: "Reason",
    label: "Reason",
    desc: "Filter or break down figures by displacement reason.",
    questions: [
      "Conflict-driven IDPs in 2024",
      "Female IDPs displaced due to Economic reason",
      "IDPs by reason in Somalia",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <path d="M6 6 L30 6 Q32 6 32 8 L32 22 Q32 24 30 24 L20 24 L14 30 L14 24 L8 24 Q6 24 6 22 L6 8 Q6 6 8 6 Z" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 13 Q15 10 18 10 Q21 10 21 13 Q21 15 18 16" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" />
        <circle cx="18" cy="19" r="1.5" fill="#0033a0" />
      </svg>
    ),
  },
  {
    key: "Origin",
    label: "Origin",
    desc: "Explore where displaced people originally came from.",
    questions: [
      "Origin of IDPs in Sudan",
      "Displacement origins within Syria",
      // "Highest number of idps with origin",
    ],
    icon: (
      <svg width="28" height="28" viewBox="0 0 36 36" fill="none">
        <circle cx="18" cy="18" r="4" stroke="#0033a0" strokeWidth="3" />
        <line x1="18" y1="4" x2="18" y2="12" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" />
        <polyline points="14,8 18,4 22,8" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <line x1="18" y1="24" x2="18" y2="32" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" />
        <polyline points="14,28 18,32 22,28" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <line x1="4" y1="18" x2="12" y2="18" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" />
        <polyline points="8,14 4,18 8,22" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <line x1="24" y1="18" x2="32" y2="18" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" />
        <polyline points="28,14 32,18 28,22" stroke="#0033a0" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    ),
  },
];

const ChipDocIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="14" height="14" rx="2" stroke="#0033a0" strokeWidth="1.5" />
    <line x1="4" y1="5" x2="12" y2="5" stroke="#0033a0" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="4" y1="8" x2="12" y2="8" stroke="#0033a0" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="4" y1="11" x2="9" y2="11" stroke="#0033a0" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const createErrorResponse = ({ code, message }) => ({
  response_type: null,
  response_type_analysis: null,
  response_status: {
    state: "error",
    code,
    title: "Error",
    type: "error",
    message,
    body: message,
    severity: "error",
    ui: {
      show_summary: true,
      show_data_tab: false,
      show_visualization_tab: false,
    },
  },
  summary: message,
  data: [],
  visualization: null,
});


const removeSqlFromDebugPayload = (response) => {
  if (
    !response?.debug?.sqb ||
    !Object.prototype.hasOwnProperty.call(response.debug.sqb, "sql")
  ) {
    return response;
  }

  const sqbWithoutSql = { ...response.debug.sqb };
  delete sqbWithoutSql.sql;

  return {
    ...response,
    debug: {
      ...response.debug,
      sqb: sqbWithoutSql,
    },
  };
};

const DTMAIAgent = ({ onClose }) => {
  const [prompt, setPrompt] = useState("");
  const [showPromptValidation, setShowPromptValidation] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);

  const conversationRef = useRef(null);
  const visualizationLoadingRef = useRef(new Set());

  // Only scroll when the conversation actually grows (a new message), never on
  // open. Tracking the previous length is StrictMode-safe (a skip-once flag gets
  // consumed by the dev double-invoke and would still scroll on open).
  const prevPromptsLenRef = useRef(prompts.length);
  useEffect(() => {
    const grew = prompts.length > prevPromptsLenRef.current;
    prevPromptsLenRef.current = prompts.length;
    if (!grew) return;
    const conversation = conversationRef.current;
    const newestMessage = conversation?.lastElementChild;

    if (!conversation || !newestMessage) return;

    newestMessage.scrollIntoView({
      block: "start",
      behavior: "smooth",
    });
  }, [prompts]);
  const [activeDomain, setActiveDomain] = useState(ANALYSIS_DOMAINS[0].key);
  const [activeIndicator, setActiveIndicator] = useState(INDICATORS[0].key);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const activeDomainMeta =
    ANALYSIS_DOMAINS.find((domain) => domain.key === activeDomain) ||
    ANALYSIS_DOMAINS[0];
  const activeIndicatorMeta =
    INDICATORS.find((indicator) => indicator.key === activeIndicator) ||
    INDICATORS[0];
  const promptValidation = useMemo(
    () => validatePromptInput(prompt),
    [prompt],
  );

  // Scroll to the end only on a new message or when a submission starts — not on
  // open. Tracks previous values so the StrictMode double-invoke can't trigger it.
  const prevEndLenRef = useRef(prompts.length);
  const prevSubmittingRef = useRef(isSubmitting);
  useEffect(() => {
    const grew = prompts.length > prevEndLenRef.current;
    const startedSubmitting = isSubmitting && !prevSubmittingRef.current;
    prevEndLenRef.current = prompts.length;
    prevSubmittingRef.current = isSubmitting;
    if (!grew && !startedSubmitting) return;
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [prompts, isSubmitting]);


  useEffect(() => {
    if (!isSubmitting) {
      setLoadingStepIndex(0);
      return undefined;
    }

    setLoadingStepIndex(0);

    const intervalId = window.setInterval(() => {
      setLoadingStepIndex((currentIndex) =>
        Math.min(currentIndex + 1, LOADING_STEPS.length - 1),
      );
    }, LOADING_STEP_DURATION_MS);

    return () => window.clearInterval(intervalId);
  }, [isSubmitting]);

  const sendPrompt = async (
    text,
    { roundAnchorReportingDate = "", explicitRoundAnchorContext } = {},
  ) => {
    const validation = validatePromptInput(text);

    if (!validation.valid || isSubmitting) {
      setShowPromptValidation(true);
      return;
    }

    const submittedPrompt = text.trim();

    const clientId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    setPrompt("");
    setShowPromptValidation(false);
    setPrompts((currentPrompts) => [
      ...currentPrompts,
      {
        clientId,
        promptId: null,
        prompt: submittedPrompt,
        response: null,
        ...initialVizState,
      },
    ]);
    setIsSubmitting(true);

    let data = null;

    try {
      const apiBase =
        API_BASE_URL ||
        (typeof window !== "undefined" ? window.location.origin : "");
      const chatUrl = `${apiBase}/api/chat`;
      const operations = await getBackendOperationMetadata({ apiBase });
      const operationHints = findOperationHints(submittedPrompt, operations);
      const requestBody = {
        prompt: submittedPrompt,
        summarize: 1,
        ...(operationHints.length > 0
          ? { operation_hints: operationHints }
          : {}),
        ...(roundAnchorReportingDate
          ? { round_anchor_reporting_date: roundAnchorReportingDate }
          : {}),
        ...(explicitRoundAnchorContext !== undefined
          ? { explicit_round_anchor_context: explicitRoundAnchorContext }
          : {}),
      };

      const response = await fetch(chatUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      data = await response.json();

      if (!response.ok && !data?.response_status) {
        data = createErrorResponse({
          code: "SERVER_ERROR",
          message: `The server returned an error (${response.status}). Please try again in a moment.`,
        });
      }

      const summaryText =
        data?.summary?.summary_text ||
        (typeof data?.summary === "string" ? data.summary : "");
      const hasResponseStatus = !!data?.response_status;
      const hasNoDataAdvisor =
        data?.no_data_advisor?.status === "no_exact_match";

      if (!summaryText && !hasNoDataAdvisor && !hasResponseStatus) {
        data = createErrorResponse({
          code: "INVALID_RESPONSE",
          message:
            "The server response could not be displayed because it did not include a summary or response status.",
        });
      }
    } catch (error) {
      data = createErrorResponse({
        code: "SERVER_UNREACHABLE",
        message:
          "Could not reach the server or parse its response. Please check that the API server is running and try again.",
      });
    }

    data = removeSqlFromDebugPayload(data);

    setPrompts((currentPrompts) =>
      currentPrompts.map((promptItem) =>
        promptItem.clientId === clientId
          ? {
              ...promptItem,
              promptId: data?.prompt_id ?? data?.promptId ?? null,
              response: data,
              ...initialVizState,
            }
          : promptItem,
      ),
    );
    setIsSubmitting(false);
  };

  const updatePromptVisualization = (clientId, updates) => {
    setPrompts((currentPrompts) =>
      currentPrompts.map((promptItem) =>
        promptItem.clientId === clientId
          ? {
              ...promptItem,
              ...updates,
            }
          : promptItem,
      ),
    );
  };

  const loadVisualizationForMessage = async (clientId) => {
    const message = prompts.find((promptItem) => promptItem.clientId === clientId);

    if (!message) return;

    if (message.vizStatus === "loading") return;

    if (message.vizStatus === "success" && message.vizSpec) return;

    if (visualizationLoadingRef.current.has(clientId)) return;

    if (!message.promptId) {
      updatePromptVisualization(clientId, {
        vizStatus: "not_available",
        vizSpec: null,
        vizMeta: null,
        vizError: {
          code: "MISSING_PROMPT_ID",
          message: "Visualization is not available for this response.",
        },
      });
      return;
    }

    visualizationLoadingRef.current.add(clientId);
    updatePromptVisualization(clientId, {
      vizStatus: "loading",
      vizError: null,
    });

    try {
      const response = await fetch(
        apiUrl(`/api/visualization/${message.promptId}`),
        { method: "POST" },
      );
      const payload = await response.json();

      if (payload?.status === "success" && payload?.viz_spec) {
        updatePromptVisualization(clientId, {
          vizStatus: "success",
          vizSpec: payload.viz_spec,
          vizMeta: payload.meta || null,
          vizError: null,
        });
        return;
      }

      if (payload?.status === "not_available") {
        updatePromptVisualization(clientId, {
          vizStatus: "not_available",
          vizSpec: null,
          vizMeta: payload.meta || null,
          vizError: payload.error || null,
        });
        return;
      }

      updatePromptVisualization(clientId, {
        vizStatus: "error",
        vizSpec: null,
        vizMeta: payload?.meta || null,
        vizError: payload?.error || {
          code: "VISUALIZATION_ERROR",
          message: "Failed to generate visualization.",
        },
      });
    } catch (error) {
      updatePromptVisualization(clientId, {
        vizStatus: "error",
        vizSpec: null,
        vizMeta: null,
        vizError: {
          code: "VISUALIZATION_ERROR",
          message: "Failed to generate visualization.",
        },
      });
    } finally {
      visualizationLoadingRef.current.delete(clientId);
    }
  };

  const submitPrompt = (event) => {
    event.preventDefault();
    setShowPromptValidation(true);
    sendPrompt(prompt);
  };

  const handleSuggestion = (suggestion) => {
    sendPrompt(suggestion);
    inputRef.current?.focus();
  };

  const handleDataGapSuggestion = (suggestion) => {
    if (!suggestion?.query_text) return;
    sendPrompt(suggestion.query_text);
    inputRef.current?.focus();
  };

  const resetChat = () => {
    setPrompts([]);
    setPrompt("");
    inputRef.current?.focus();
  };

  return (
    <section className={classes.agent} aria-label="DTM API AI Agent">
      <header className={classes.header}>
        <div className={classes.title_group}>
          <h2 className={classes.title}>AI Agent - Ask Seb</h2>
          <span className={classes.badge}>BETA</span>
        </div>
        <div className={classes.header_actions}>
          <button
            type="button"
            className={classes.feedback_button}
            aria-label="Send feedback"
            onClick={() => setIsFeedbackOpen(true)}
          >
            Feedback
          </button>
          {prompts.length > 0 && (
            <button
              type="button"
              className={classes.new_chat_button}
              aria-label="New chat"
              title="New chat"
              onClick={resetChat}
            >
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </button>
          )}
          <button
            type="button"
            className={classes.close_button}
            aria-label="Close"
            onClick={onClose}
          >
            <span aria-hidden="true" />
          </button>
        </div>
      </header>

      <GeneralFeedbackModal
        open={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />

      <div ref={conversationRef} className={classes.conversation}>
        {prompts.length === 0 ? (
          <div className={classes.landing}>
            <div className={classes.landing_intro}>
              <img
                src="/images/ask-seb.png"
                alt="Ask Seb"
                className={classes.landing_intro_image}
              />
              <div className={classes.landing_intro_copy}>
                <h3 className={classes.landing_intro_title}>How can I help?</h3>
                <p className={classes.landing_intro_text}>
                  Ask me about internally displaced population data — figures,
                  trends, drivers and locations. Pick an analysis domain below to
                  see example prompts, or type your own question.
                </p>
              </div>
            </div>

            <section className={classes.landing_section}>
              <p className={classes.landing_eyebrow}>Analysis domains</p>
              <p className={classes.landing_sub}>
                I can assist you on the following IDP analysis domains
              </p>

              <div className={classes.domain_tabs}>
                {ANALYSIS_DOMAINS.map((domain) => (
                  <button
                    type="button"
                    key={domain.key}
                    className={`${classes.domain_tab}${
                      domain.key === activeDomain ? ` ${classes.domain_tab_active}` : ""
                    }`}
                    onClick={() => setActiveDomain(domain.key)}
                  >
                    <span className={classes.domain_icon}>{domain.icon}</span>
                    <span className={classes.domain_label}>{domain.label}</span>
                  </button>
                ))}
              </div>

              <p className={classes.domain_desc}>{activeDomainMeta.desc}</p>

              <div className={classes.domain_chips}>
                {activeDomainMeta.questions.map((question) => (
                  <button
                    type="button"
                    key={question}
                    className={classes.prompt_chip}
                    onClick={() => handleSuggestion(question)}
                  >
                    <ChipDocIcon />
                    <span>{question}</span>
                  </button>
                ))}
              </div>
            </section>

            <hr className={classes.landing_divider} />

            <section className={classes.landing_section}>
              <p className={classes.landing_eyebrow}>Available indicators</p>
              <p className={classes.landing_sub}>
                Use any of the following in your prompt
              </p>

              <div className={classes.domain_tabs}>
                {INDICATORS.map((indicator) => (
                  <button
                    type="button"
                    key={indicator.key}
                    className={`${classes.domain_tab}${
                      indicator.key === activeIndicator ? ` ${classes.domain_tab_active}` : ""
                    }`}
                    onClick={() => setActiveIndicator(indicator.key)}
                  >
                    <span className={classes.domain_icon}>{indicator.icon}</span>
                    <span className={classes.domain_label}>{indicator.label}</span>
                  </button>
                ))}
              </div>

              <p className={classes.domain_desc}>{activeIndicatorMeta.desc}</p>

              <div className={classes.domain_chips}>
                {activeIndicatorMeta.questions.map((question) => (
                  <button
                    type="button"
                    key={question}
                    className={classes.prompt_chip}
                    onClick={() => handleSuggestion(question)}
                  >
                    <ChipDocIcon />
                    <span>{question}</span>
                  </button>
                ))}
              </div>
            </section>
          </div>
        ) : (
          prompts.map((promptItem) => (
            <React.Fragment key={promptItem.clientId}>
              <UserPrompt
                prompt={promptItem.prompt}
                promptId={promptItem.promptId}
              />
              {promptItem.response && (
                <AIResponse
                  promptId={promptItem.promptId}
                  prompt={promptItem.prompt}
                  response={promptItem.response}
                  vizStatus={promptItem.vizStatus}
                  vizSpec={promptItem.vizSpec}
                  vizError={promptItem.vizError}
                  vizMeta={promptItem.vizMeta}
                  onLoadVisualization={() =>
                    loadVisualizationForMessage(promptItem.clientId)
                  }
                  onPromptSubmit={sendPrompt}
                  onSuggestionClick={handleDataGapSuggestion}
                />
              )}
            </React.Fragment>
          ))
        )}

        {isSubmitting && (
          <div className={classes.typing} aria-live="polite">
            <span className={classes.typing_dots} aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
            <span className={classes.loading_label}>
              {LOADING_STEPS[loadingStepIndex]}
            </span>
          </div>
        )}

        <div ref={endRef} />
      </div>

      <form className={classes.prompt_bar} onSubmit={submitPrompt}>
        <div
          className={`${classes.prompt_shell}${
            showPromptValidation && !promptValidation.valid
              ? ` ${classes.prompt_shell_invalid}`
              : ""
          }`}
        >
          <input
            ref={inputRef}
            className={classes.prompt_input}
            type="text"
            value={prompt}
            onChange={(event) => {
              setPrompt(event.target.value);
              setShowPromptValidation(true);
            }}
            placeholder="Ask DTM..."
            aria-label="Ask about IDP data"
            aria-invalid={showPromptValidation && !promptValidation.valid}
            aria-describedby="dtm-prompt-feedback"
          />
          <button
            type="submit"
            className={classes.send_button}
            aria-label="Send"
            disabled={isSubmitting || !promptValidation.valid}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14" />
              <path d="M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>
        <div
          id="dtm-prompt-feedback"
          className={classes.prompt_feedback}
          aria-live="polite"
        >
          <span
            className={`${classes.prompt_validation}${
              showPromptValidation && !promptValidation.valid
                ? ` ${classes.prompt_validation_visible}`
                : ""
            }`}
          >
            {showPromptValidation && !promptValidation.valid
              ? promptValidation.message
              : ""}
          </span>
          <span
            className={`${classes.prompt_counter}${
              prompt.length > MAX_PROMPT_CHARS
                ? ` ${classes.prompt_counter_invalid}`
                : ""
            }`}
          >
            {prompt.length} / {MAX_PROMPT_CHARS}
          </span>
        </div>
        <p className={classes.disclaimer}>
          DTM AI Agent is in beta and can make mistakes. Verify important figures.
        </p>
      </form>
    </section>
  );
};

export default DTMAIAgent;
